<!--
  地图容器组件
  功能：
  1. 显示百度地图
  2. 支持点位选择和连线
  3. 支持起点和终点标记
  4. 支持地图控制（重置、撤销、重做、地图类型切换、全屏）
  5. 支持点位信息显示
  6. 支持文件树交互
  7. 支持文件下载
-->
<template>
  <div class="container">
    <!-- 地图容器 -->
    <div class="map" id="map"></div>
    
    <!-- 文件树组件 -->
    <tree class="tree" ref="tree" :start="start" :end="end" @treeSelected="handleSelected" @download="openModal" @reset="reset" />
    
    <!-- 文件选择模态框 -->
    <FileDownloadConfirmModal ref="fileDownloadConfirmModal"></FileDownloadConfirmModal>
    
    <!-- 页脚信息 -->
    <div class="footer">
      <a href="https://beian.miit.gov.cn/">鄂ICP备2024059729号-1</a>
      <span style="margin-left: 10px;">Beijing [2024] 1034-029 ©2028</span>
    </div>

    <!-- 文件点过滤模态框 -->
    <FilePointFilterModal ref="filePointFilterModal" @ok="addTreeSelectNode"></FilePointFilterModal>
    
    <!-- 地图控制面板 -->
    <div class="map-controls">
      <div class="control-group">
        <button class="control-btn" @click="reset" :title="$t('reset')">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="control-btn" @click="undo" :disabled="currentHistoryIndex <= 0" :title="$t('undo')">
          <i class="fas fa-undo"></i>
        </button>
        <button class="control-btn" @click="redo" :disabled="currentHistoryIndex >= history.length - 1" :title="$t('redo')">
          <i class="fas fa-redo"></i>
        </button>
      </div>
      <div class="control-group">
        <button class="control-btn" @click="toggleMapType" :title="$t('toggleMapType')">
          <i class="fas fa-layer-group"></i>
        </button>
        <button class="control-btn" @click="toggleFullscreen" :title="isFullscreen ? $t('exitFullscreen') : $t('fullscreen')">
          <i class="fas" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
        </button>
      </div>
    </div>

    <!-- 选择模式指示器 -->
    <div class="selection-indicator" :class="{ 'selecting': selectionMode === 'selecting' }">
      <div class="indicator-content">
        <i class="fas" :class="selectionMode === 'selecting' ? 'fa-mouse-pointer' : 'fa-hand-pointer'"></i>
        <span>{{ selectionMode === 'selecting' ? $t('selectEndPoint') : $t('selectStartPoint') }}</span>
      </div>
    </div>

    <!-- 浮动信息面板 -->
    <div class="floating-info" v-if="start || end">
      <div class="info-header">
        <span class="info-title">{{ $t('pointInfo') }}</span>
      </div>
      <div class="info-content">
        <!-- 起点信息 -->
        <div class="point-info" v-if="start">
          <div class="point-header">
            <i class="fas fa-flag-checkered"></i>
            <span>{{ $t('startPointInfo') }}</span>
          </div>
          <div class="info-item">
            <i class="fas fa-clock"></i>
            <span>{{ $t('time') }}：{{ start.data }}</span>
          </div>
          <div class="info-item">
            <i class="fas fa-map-marker-alt"></i>
            <span>{{ $t('longitude') }}：{{ start.longitude }}</span>
          </div>
          <div class="info-item">
            <i class="fas fa-map-marker-alt"></i>
            <span>{{ $t('latitude') }}：{{ start.latitude }}</span>
          </div>
          <div class="info-actions">
            <button class="action-btn delete" @click="removePoint(start)">
              <i class="fas fa-trash"></i> {{ $t('deleteStartPoint') }}
            </button>
          </div>
        </div>

        <!-- 终点信息 -->
        <div class="point-info" v-if="end">
          <div class="point-header">
            <i class="fas fa-flag"></i>
            <span>{{ $t('endPointInfo') }}</span>
          </div>
          <div class="info-item">
            <i class="fas fa-clock"></i>
            <span>{{ $t('time') }}：{{ end.data }}</span>
          </div>
          <div class="info-item">
            <i class="fas fa-map-marker-alt"></i>
            <span>{{ $t('longitude') }}：{{ end.longitude }}</span>
          </div>
          <div class="info-item">
            <i class="fas fa-map-marker-alt"></i>
            <span>{{ $t('latitude') }}：{{ end.latitude }}</span>
          </div>
          <div class="info-actions">
            <button class="action-btn delete" @click="removePoint(end)">
              <i class="fas fa-trash"></i> {{ $t('deleteEndPoint') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 操作提示 -->
    <div class="operation-tips" v-if="hoverPoint">
      <div class="tip-content">
        <p>{{ $t('clickToSelect') }}</p>
        <p class="tip-detail">{{ $t('time') }}: {{ hoverPoint.data }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import tree from "@/components/tree.vue"
import FileDownloadConfirmModal from "./FileDownloadConfirmModal.vue";
import {getFileTree} from "@/request/api";
import northArrow from "@/assets/north-arrow.png";
import { Modal } from 'ant-design-vue';
import FilePointFilterModal from "@/components/FilePointFilterModal.vue";
// 引入本地的 Font Awesome CSS
import '@/assets/css/all.min.css';

export default {
  name: 'container',
  components: {
    tree, 
    FileDownloadConfirmModal, 
    FilePointFilterModal
  },
  model: {
    prop: 'mouseInMap',
    event: 'change'
  },
  props: {
    mouseInMap: {
      type: Boolean
    }
  },
  data() {
    return {
      list: [], // 点位列表
      pointList: [], // 地图点位列表
      polylines: [], // 连线列表
      map: null, // 地图实例
      start: "", // 起点信息
      end: "", // 终点信息
      startIndex: 0, // 起点索引
      endIndex: 0, // 终点索引
      id: "", // 当前选中的文件ID
      treeData: [], // 文件树数据
      filteredKeys: [], // 过滤后的键
      startMarker: {}, // 起点标记
      endMarker: {}, // 终点标记
      filteredNodes: [], // 过滤后的节点
      history: [], // 操作历史记录
      currentHistoryIndex: -1, // 当前历史记录索引
      maxHistoryLength: 10, // 最大历史记录长度
      selectionMode: 'idle', // 选择模式：idle-空闲，selecting-选择中
      hoverPoint: null, // 悬停的点位
      selectedPoint: null, // 选中的点位
      isFullscreen: false, // 是否全屏
      currentMapType: BMAP_SATELLITE_MAP, // 当前地图类型
    }
  },
  mounted() {
    this.initMap();

    // 监听地图鼠标事件
    const map = document.getElementById('map');
    map.addEventListener('mouseenter', () => {
      this.$emit('change',true)
    });
    map.addEventListener('mouseleave', () => {
      this.$emit('change',false)
    });

    // 监听全屏变化
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
  },
  methods: {
    /**
     * 打开下载模态框
     * @param {Array} arr - 文件ID数组
     */
    openModal(arr) {
      this.$refs.fileDownloadConfirmModal.open(arr,this.start,this.end)
    },
    
    /**
     * 初始化地图
     */
    initMap() {
      const map = new BMap.Map("map");
      this.map = map;
      const point = new BMap.Point(116.404, 39.915);
      map.centerAndZoom(point, 15);
      map.enableScrollWheelZoom(true);
      map.setMapType(BMAP_SATELLITE_MAP);

      // 添加比例尺
      map.addControl(new BMap.ScaleControl({
        anchor: BMAP_ANCHOR_BOTTOM_RIGHT
      }));

      // 添加默认缩放平移控件
      map.addControl(new BMap.NavigationControl({
        anchor: BMAP_ANCHOR_BOTTOM_RIGHT,
      }));

      // 添加地图类型切换控件
      map.addControl(new BMap.MapTypeControl({
        anchor: BMAP_ANCHOR_TOP_RIGHT,
        type: [BMAP_NORMAL_MAP, BMAP_SATELLITE_MAP, BMAP_HYBRID_MAP]
      }));

      this.addNorthArrow();

      // 添加地图事件监听
      map.addEventListener('click', () => {
        this.selectedPoint = null;
      });

      // 添加地图加载完成事件
      map.addEventListener('load', () => {
        this.$message.success(this.$t('mapLoaded'));
      });
    },
    
    /**
     * 添加指北针
     */
    addNorthArrow(){
      // 创建自定义控件构造函数
      const ImageControl = function() {
        // 控件默认位置
        this.defaultAnchor = BMAP_ANCHOR_BOTTOM_RIGHT;
        // 控件偏移量
        this.defaultOffset = new BMap.Size(22, 210);
      }
      ImageControl.prototype = new BMap.Control();
      // 实现控件初始化方法
      ImageControl.prototype.initialize = function(map) {
        const img = document.createElement("img");
        img.src = northArrow;   // 替换为你的图片路径
        img.style.cursor = "pointer";
        img.style.width = "55px";     // 控制图片尺寸
        img.style.height = "55px";

        // 绑定点击事件（可选）
        img.addEventListener("click", ()=> {
        });

        map.getContainer().appendChild(img); // 将控件添加到地图容器
        return img;
      };

      this.map.addControl(new ImageControl());
    },

    /**
     * 重置地图状态
     */
    reset() {
      this.start = ''
      this.end = ''
      this.startIndex = 0
      this.endIndex = 0
      this.startMarker = {}
      this.endMarker = {}
      this.map.clearOverlays()
      this.history = []
      this.currentHistoryIndex = -1
      this.selectionMode = 'idle'
      this.hoverPoint = null
    },

    /**
     * 保存当前状态到历史记录
     */
    saveToHistory() {
      const currentState = {
        start: this.start,
        end: this.end,
        startIndex: this.startIndex,
        endIndex: this.endIndex,
        startMarker: this.startMarker,
        endMarker: this.endMarker,
        polylines: [...this.polylines]
      }

      if (this.currentHistoryIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.currentHistoryIndex + 1)
      }

      this.history.push(currentState)
      this.currentHistoryIndex = this.history.length - 1

      if (this.history.length > this.maxHistoryLength) {
        this.history.shift()
        this.currentHistoryIndex--
      }
    },

    /**
     * 撤销操作
     */
    undo() {
      if (this.currentHistoryIndex > 0) {
        this.currentHistoryIndex--;
        this.restoreState(this.history[this.currentHistoryIndex]);
        this.$message.info(this.$t('operationUndone'));
      }
    },

    /**
     * 重做操作
     */
    redo() {
      if (this.currentHistoryIndex < this.history.length - 1) {
        this.currentHistoryIndex++;
        this.restoreState(this.history[this.currentHistoryIndex]);
        this.$message.info(this.$t('operationRedone'));
      }
    },

    /**
     * 恢复历史状态
     * @param {Object} state - 历史状态对象
     */
    restoreState(state) {
      this.map.clearOverlays()
      this.polylines = []

      if (state.start) {
        this.start = state.start
        this.startIndex = state.startIndex
        this.startMarker = state.startMarker
        this.map.addOverlay(this.startMarker)
      }

      if (state.end) {
        this.end = state.end
        this.endIndex = state.endIndex
        this.endMarker = state.endMarker
        this.map.addOverlay(this.endMarker)
      }

      state.polylines.forEach(polyline => {
        this.map.addOverlay(polyline)
        this.polylines.push(polyline)
      })
    },

    /**
     * 添加点位选择动画效果
     * @param {Object} point - 点位数据
     * @param {Boolean} isStart - 是否为起点
     */
    addPointAnimation(point, isStart = true) {
      const marker = isStart ? this.startMarker : this.endMarker;
      if (marker) {
        marker.setAnimation(BMAP_ANIMATION_BOUNCE);
        setTimeout(() => {
          marker.setAnimation(null);
        }, 1000);
      }
    },

    /**
     * 处理点位点击事件
     * @param {Object} data - 点位数据
     */
    pointClick(data) {
      // 如果已经在选择模式，且点击了已选择的点，则取消选择
      if (this.selectionMode === 'selecting' && this.start && this.start.index === data.index) {
        this.cleanMarker();
        this.clearDrawLines();
        this.selectionMode = 'idle';
        this.$message.info(this.$t('selectionCancelled'));
        return;
      }

      let icon;
      if (!this.start) {
        // 选择起点
        this.start = data;
        this.startIndex = data.index;
        icon = new BMap.Icon(require("@/assets/flag-start.png"), new BMap.Size(26, 41),{
          imageSize: new BMap.Size(26, 41)
        });
        this.startMarker = new BMap.Marker(new BMap.Point(data.longitude, data.latitude), {
          icon: icon,
          offset: new BMap.Size(10, -18)
        });
        this.map.addOverlay(this.startMarker);
        this.openWindow(data);
        this.startMarker.addEventListener('click', (e) => {
          this.openWindow(data);
        });

        // 重新添加终点标记
        if (this.end) {
          icon = new BMap.Icon(require("@/assets/flag-end.png"), new BMap.Size(26, 41),{
            imageSize: new BMap.Size(26, 41)
          });
          this.endMarker = new BMap.Marker(new BMap.Point(this.end.longitude, this.end.latitude), {
            icon: icon,
            offset: new BMap.Size(10, -18)
          });
          this.map.addOverlay(this.endMarker);
          this.endMarker.addEventListener('click', (e) => {
            this.openWindow(this.end);
          });
          
          // 重新绘制连线
          this.drawRangePoints(this.startIndex, this.endIndex);
        }

        this.saveToHistory();
        this.selectionMode = 'selecting';
        this.addPointAnimation(data, true);
        this.$message.success(this.$t('newStartPointSelected'));
      } else if (!this.end) {
        // 如果点击的是起点，则取消选择
        if (this.start.index === data.index) {
          this.cleanMarker();
          this.clearDrawLines();
          this.selectionMode = 'idle';
          this.$message.info(this.$t('selectionCancelled'));
          return;
        }

        // 选择终点
        this.end = data;
        this.endIndex = data.index;
        var start = this.formatTime(this.parseDate(this.start.data));
        var end = this.formatTime(this.parseDate(this.end.data));
        if(start > end){
          // 如果终点时间早于起点时间，交换起点和终点
          var time = this.start;
          this.start = this.end;
          this.end = time;
          // 交换索引
          var tempIndex = this.startIndex;
          this.startIndex = this.endIndex;
          this.endIndex = tempIndex;
          // 更新标记
          this.map.removeOverlay(this.startMarker);
          this.map.removeOverlay(this.endMarker);
          // 重新添加标记
          icon = new BMap.Icon(require("@/assets/flag-start.png"), new BMap.Size(26, 41),{
            imageSize: new BMap.Size(26, 41)
          });
          this.startMarker = new BMap.Marker(new BMap.Point(this.start.longitude, this.start.latitude), {
            icon: icon,
            offset: new BMap.Size(10, -18)
          });
          icon = new BMap.Icon(require("@/assets/flag-end.png"), new BMap.Size(26, 41),{
            imageSize: new BMap.Size(26, 41)
          });
          this.endMarker = new BMap.Marker(new BMap.Point(this.end.longitude, this.end.latitude), {
            icon: icon,
            offset: new BMap.Size(10, -18)
          });
          this.map.addOverlay(this.startMarker);
          this.map.addOverlay(this.endMarker);
        } else {
          icon = new BMap.Icon(require("@/assets/flag-end.png"), new BMap.Size(26, 41),{
            imageSize: new BMap.Size(26, 41)
          });
          this.endMarker = new BMap.Marker(new BMap.Point(data.longitude, data.latitude), {
            icon: icon,
            offset: new BMap.Size(10, -18)
          });
          this.map.addOverlay(this.endMarker);
        }
        
        this.drawRangePoints(this.startIndex, this.endIndex);
        this.pointConfirm();
        this.openWindow(data);
        this.endMarker.addEventListener('click', (e) => {
          this.openWindow(data);
        });
        this.saveToHistory();
        this.selectionMode = 'idle';
        this.addPointAnimation(data, false);
        this.$message.success(this.$t('endPointSelected'));
      } else {
        // 如果已经有点位，点击新的点位时，将其设为新的起点
        this.cleanMarker();
        this.clearDrawLines();
        this.start = data;
        this.startIndex = data.index;
        icon = new BMap.Icon(require("@/assets/flag-start.png"), new BMap.Size(26, 41),{
          imageSize: new BMap.Size(26, 41)
        });
        this.startMarker = new BMap.Marker(new BMap.Point(data.longitude, data.latitude), {
          icon: icon,
          offset: new BMap.Size(10, -18)
        });
        this.map.addOverlay(this.startMarker);
        this.openWindow(data);
        this.startMarker.addEventListener('click', (e) => {
          this.openWindow(data);
        });

        // 重新添加终点标记
        if (this.end) {
          icon = new BMap.Icon(require("@/assets/flag-end.png"), new BMap.Size(26, 41),{
            imageSize: new BMap.Size(26, 41)
          });
          this.endMarker = new BMap.Marker(new BMap.Point(this.end.longitude, this.end.latitude), {
            icon: icon,
            offset: new BMap.Size(10, -18)
          });
          this.map.addOverlay(this.endMarker);
          this.endMarker.addEventListener('click', (e) => {
            this.openWindow(this.end);
          });
          
          // 重新绘制连线
          this.drawRangePoints(this.startIndex, this.endIndex);
        }

        this.saveToHistory();
        this.selectionMode = 'selecting';
        this.addPointAnimation(data, true);
        this.$message.success(this.$t('newStartPointSelected'));
      }
    },
    
    /**
     * 打开信息窗口
     * @param {Object} data - 点位数据
     */
    openWindow(data){
      this.selectedPoint = data;
    },
    
    /**
     * 清除标记
     */
    cleanMarker(){
      if (this.endMarker) {
        this.map.removeOverlay(this.endMarker);
        this.endMarker = null;
      }
      if (this.startMarker) {
        this.map.removeOverlay(this.startMarker);
        this.startMarker = null;
      }
      this.end = '';
      this.start = '';
      this.endIndex = 0;
      this.startIndex = 0;
    },
    
    /**
     * 处理文件树选择事件
     * @param {Array} data - 点位数据数组
     * @param {Array} id - 文件ID数组
     */
    handleSelected(data, id) {
      const map = this.map;

      //移除旧地图点
      this.reset()

      //打上新地图点
      this.$nextTick(() => {
        if(data.length === 0){
          return;
        }
        this.list = data
        this.id = id[0]
        if(this.list.length !=0){
          const point = new BMap.Point(this.list[0].longitude, this.list[0].latitude);
          map.centerAndZoom(point, 20);
        }
        map.enableScrollWheelZoom(true);
        map.setMapType(BMAP_SATELLITE_MAP);

        if (document.createElement('canvas').getContext) {  // 判断当前浏览器是否支持绘制海量点
          var points = [];  // 添加海量点数据
          for (var i = 0; i < this.list.length; i++) {
            var point= new BMap.Point(this.list[i].longitude, this.list[i].latitude)
            point.data = this.list[i]
            point.data.index = i
            points.push(point);
          }
          this.pointList = points
          var options = {
            size: 20,
            color: '#fff566'
          }
          var pc = new BMap.PointCollection(points, options);  // 初始化PointCollection
          pc.addEventListener('click', (e) => {
            this.pointClick(e.point.data);
          });
          map.addOverlay(pc);
        } else {
          alert('请在chrome、safari、IE8+以上浏览器查看本示例');
        }
      })
    },

    /**
     * 添加树选择节点
     * @param {Array} data - 节点数据
     */
    addTreeSelectNode(data, aa, bb){
      this.$refs.tree.addSelectNode(data);
    },
    
    /**
     * 递归过滤节点
     * @param {Array} nodes - 节点数组
     * @param {String} startTime - 开始时间
     * @param {String} endTime - 结束时间
     */
    filterNodesRecursively(nodes, startTime, endTime) {
      nodes.forEach(node => {
        node.name = node.name.split(" ")[0]
        const list = []
        let underscoreIndex = node.name.indexOf('_');
        if (underscoreIndex !== -1) {
          let part1 = node.name.substring(0, underscoreIndex);
          let part2 = node.name.substring(underscoreIndex + 1);
          list.push(part1)
          list.push(part2)
        } else {
          // 如果没有找到下划线，则整个字符串作为第一部分
          list.push(node.name)
        }
        
        if (list.length != 2) {
          if (node.children && node.children.length > 0) {
            this.filterNodesRecursively(node.children, startTime, endTime);
          }
          return
        }

        if (!list[1].endsWith('.bag')) {
          if (node.children && node.children.length > 0) {
            this.filterNodesRecursively(node.children, startTime, endTime);
          }
          return
        }

        var name = this.convertDateFormat(list[0]);
        name = name.replace(/\/+/g, '-')
        name = name.replace(/\./g, ':')
        var bool = this.isTimeInRange(name, startTime, endTime)
        if (bool) {
          if(node.children.length==0){
            // 只需要叶子节点
            this.filteredNodes.push(node)
          }
        }

        if (node.children && node.children.length > 0) {
          this.filterNodesRecursively(node.children, startTime, endTime);
        }
      });
    },
    
    /**
     * 清除所有已画的线
     */
    clearDrawLines() {
      this.polylines.forEach(polyline => {
        this.map.removeOverlay(polyline);
      });

      // 清空polylines数组
      this.polylines.length = 0;
    },
    
    /**
     * 绘制区间范围内的线
     * @param {Number} index1 - 起点索引
     * @param {Number} index2 - 终点索引
     */
    drawRangePoints(index1, index2) {
      let start = index1 < index2 ? index1 : index2
      let end = index1 < index2 ? index2 : index1
      let points = this.pointList.slice(start, end + 1);
      const polyline = new BMap.Polyline(points, {strokeColor: "#FF0000", strokeWeight: 5, strokeOpacity: 1});
      this.map.addOverlay(polyline);
      this.polylines.push(polyline);
    },

    /**
     * 解析日期字符串
     * @param {String} timeStr - 日期字符串
     * @returns {Date} - 解析后的日期对象
     */
    parseDate(timeStr) {
      const parts = timeStr.split(/[- :]/);
      return new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
    },
    
    /**
     * 判断时间是否在某个时间范围
     * @param {String} timeStr - 时间字符串
     * @param {String} startTimeStr - 开始时间字符串
     * @param {String} endTimeStr - 结束时间字符串
     * @returns {Boolean} - 是否在范围内
     */
    isTimeInRange(timeStr, startTimeStr, endTimeStr) {
      const time = this.parseDate(timeStr);
      const startTime = this.parseDate(startTimeStr);
      const endTime = this.parseDate(endTimeStr);
      return time >= startTime && time <= endTime;
    },
    
    /**
     * 时间格式转换
     * @param {String} inputString - 输入的时间字符串
     * @returns {String} - 转换后的时间字符串
     */
    convertDateFormat(inputString) {
      const regex = /^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/;
      const match = inputString.match(regex);

      if (!match) {
        return "";
      }

      const [year, month, day, hour, minute, second] = match.slice(1);

      return `${year}/${month}/${day} ${hour}.${minute}.${second}`;
    },
    
    /**
     * 时间取整
     * @param {String} startTimeStr - 开始时间字符串
     * @param {String} endTimeStr - 结束时间字符串
     * @returns {Array} - 调整后的时间数组
     */
    adjustTimes(startTimeStr, endTimeStr) {
      // 解析时间字符串
      const startTime = this.parseTime(startTimeStr);
      const endTime = this.parseTime(endTimeStr);

      // 调整开始时间为整分钟
      const adjustedStartTime = new Date(
          startTime.getFullYear(),
          startTime.getMonth(),
          startTime.getDate(),
          startTime.getHours(),
          startTime.getMinutes(),
          0
      );

      // 计算结束时间的下一分钟
      const adjustedEndTime = new Date(
          endTime.getFullYear(),
          endTime.getMonth(),
          endTime.getDate(),
          endTime.getHours(),
          endTime.getMinutes() + 1,
          0
      );

      // 格式化调整后的时间
      const formattedStartTime = this.formatTime(adjustedStartTime);
      const formattedEndTime = this.formatTime(adjustedEndTime);

      return [formattedStartTime, formattedEndTime];
    },
    
    /**
     * 解析时间字符串
     * @param {String} timeStr - 时间字符串
     * @returns {Date} - 解析后的日期对象
     */
    parseTime(timeStr) {
      const parts = timeStr.split(/\D+/);
      return new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
    },
    
    /**
     * 格式化日期对象
     * @param {Date} date - 日期对象
     * @returns {String} - 格式化后的日期字符串
     */
    formatTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');

      return `${year}/${month}/${day} ${hour}.${minute}.${second}`;
    },

    /**
     * 确认选择范围
     */
    pointConfirm(){
      Modal.confirm({
        title: this.$t('confirmRange'),
        cancelText: this.$t('cancel'),
        okText: this.$t('confirm'),
        onOk:()=>{
          //自动时间段内的文件
          const timeList = this.adjustTimes(this.start.data, this.end.data)
          this.filteredNodes = [];
          getFileTree().then(res => {
            this.treeData = res.data;
            timeList[0] = timeList[0].replace(/\/+/g, '-')
            timeList[0] = timeList[0].replace(/\./g, ':')
            timeList[1] = timeList[1].replace(/\/+/g, '-')
            timeList[1] = timeList[1].replace(/\./g, ':')
            this.filterNodesRecursively(this.treeData[0].children[0].children, timeList[0], timeList[1]);

            this.$refs.filePointFilterModal.open(this.filteredNodes)
          })
        },
      });
    },
    
    /**
     * 处理点位悬停
     * @param {Object} point - 点位数据
     */
    handlePointHover(point) {
      if (this.selectionMode === 'selecting' && !this.end) {
        this.hoverPoint = point;
        // 可以在这里添加悬停效果，比如改变鼠标样式或显示提示
        document.body.style.cursor = 'pointer';
      }
    },

    /**
     * 处理点位离开
     */
    handlePointLeave() {
      this.hoverPoint = null;
      document.body.style.cursor = 'default';
    },

    /**
     * 删除点位
     * @param {Object} point - 点位数据
     */
    removePoint(point) {
      const isStart = point === this.start;
      const title = isStart ? this.$t('deleteStartPoint') : this.$t('deleteEndPoint');
      const content = isStart ? this.$t('confirmDeleteStart') : this.$t('confirmDeleteEnd');

      Modal.confirm({
        title: title,
        content: content,
        okText: this.$t('confirm'),
        cancelText: this.$t('cancel'),
        onOk: () => {
          if (isStart) {
            // 删除起点
            this.start = '';
            this.startIndex = 0;
            if (this.startMarker) {
              this.map.removeOverlay(this.startMarker);
              this.startMarker = null;
            }
          } else {
            // 删除终点
            this.end = '';
            this.endIndex = 0;
            if (this.endMarker) {
              this.map.removeOverlay(this.endMarker);
              this.endMarker = null;
            }
          }
          
          // 清除连线
          this.clearDrawLines();
          
          // 如果两个点都被删除，重置选择模式
          if (!this.start && !this.end) {
            this.selectionMode = 'idle';
          }
          
          this.$message.success(isStart ? this.$t('startPointDeleted') : this.$t('endPointDeleted'));
        }
      });
    },

    /**
     * 切换地图类型
     */
    toggleMapType() {
      const mapTypes = [BMAP_NORMAL_MAP, BMAP_SATELLITE_MAP, BMAP_HYBRID_MAP];
      const currentIndex = mapTypes.indexOf(this.currentMapType);
      const nextIndex = (currentIndex + 1) % mapTypes.length;
      this.currentMapType = mapTypes[nextIndex];
      this.map.setMapType(this.currentMapType);
    },

    /**
     * 切换全屏
     */
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        this.isFullscreen = true;
      } else {
        document.exitFullscreen();
        this.isFullscreen = false;
      }
    },

    /**
     * 监听全屏变化
     */
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },

    /**
     * 关闭信息面板
     */
    closeInfoPanel() {
      this.selectedPoint = null;
    },
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
  },
}
</script>

<style scoped lang="scss">
.container {
  width: 100%;
  height: 100vh;
  position: relative;
  scroll-snap-align: start;
  overflow: hidden;

  .map {
    width: 100%;
    height: 100%;
  }
  .tree {
    position: absolute; 
    top: 20px; left: 20px;
    z-index: 1000;

    :deep(.ant-btn-primary) {
      background-color: #1890ff;
      border-color: #1890ff;
      color: white;
      font-weight: 500;
      padding: 8px 16px;
      height: auto;
      box-shadow: 0 2px 8px rgba(24, 144, 255, 0.35);
      transition: all 0.3s;

      &:hover {
        background-color: #40a9ff;
        border-color: #40a9ff;
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.45);
        transform: translateY(-1px);
      }

      &:active {
        background-color: #096dd9;
        border-color: #096dd9;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.25);
        transform: translateY(0);
      }
    }
  }

  .footer{
    line-height: 30px;
    color: #fff;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    a{
      color: #fff;
      //text-decoration: underline;
    }
  }

  .map-controls {
    position: absolute;
    top: 40px;
    right: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: 1000;

    .control-group {
      display: flex;
      flex-direction: column;
      gap: 5px;
      background: white;
      padding: 5px;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .control-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: none;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333;
      transition: all 0.3s;
      position: relative;

      &:hover {
        background-color: #f5f5f5;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      i {
        font-size: 16px;
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &::after {
        content: attr(title);
        position: absolute;
        bottom: -25px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s;
      }

      &:hover::after {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  .selection-indicator {
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(5px);
    border: 1px solid #e8e8e8;
    padding: 8px 16px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;

    &.selecting {
      background-color: #e6f7ff;
      border-color: #91d5ff;
    }

    .indicator-content {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #333;

      i {
        font-size: 16px;
      }
    }
  }

  .operation-tips {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 14px;
    pointer-events: none;
    z-index: 1000;

    .tip-content {
      p {
        margin: 0;
        line-height: 1.5;
      }

      .tip-detail {
        font-size: 12px;
        opacity: 0.8;
      }
    }
  }

  .floating-info {
    position: absolute;
    top: 40px;
    right: 80px;
    width: 300px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;

    .info-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;

      .info-title {
        font-size: 16px;
        font-weight: 500;
        color: #333;
      }

      .close-btn {
        background: none;
        border: none;
        color: #999;
        cursor: pointer;
        padding: 4px;
        
        &:hover {
          color: #666;
        }
      }
    }

    .info-content {
      padding: 16px;

      .point-info {
        padding: 12px;
        border-radius: 6px;
        background: #f8f8f8;
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .point-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e8e8e8;

          i {
            color: #1890ff;
            font-size: 16px;
          }

          span {
            font-weight: 500;
            color: #333;
          }
        }
      }

      .info-item {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        
        i {
          width: 20px;
          color: #1890ff;
          margin-right: 8px;
        }

        span {
          color: #333;
        }
      }

      .info-actions {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #e8e8e8;
        text-align: right;

        .action-btn {
          padding: 6px 12px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          
          &.delete {
            background-color: #ff4d4f;
            color: white;
            
            &:hover {
              background-color: #ff7875;
            }
          }
        }
      }
    }
  }
}
</style>

<style>
.container #map svg{
  z-index: 1;
}

.info-window {
  padding: 10px;
  
  .info-content {
    margin-bottom: 10px;
    
    p {
      margin: 5px 0;
      color: #333;
    }
  }
  
  .info-actions {
    text-align: right;
    
    .remove-btn {
      padding: 4px 12px;
      background-color: #ff4d4f;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      
      &:hover {
        background-color: #ff7875;
      }
    }
  }
}
</style>
